import {Autocomplete, Box, TextField, Typography, createFilterOptions} from '@mui/material';
import {FC} from 'react';
import theme from 'src/theme';
import {toolbarSelectSx} from 'src/components/tables/tableToolbar.sx';
import {ToolbarSearchOption, ToolbarSearchSelectProps} from './ToolbarSearchSelect.types';

const filter = createFilterOptions<ToolbarSearchOption>({
    stringify: (option) => `${option.label} ${option.hint ?? ''}`,
});

/**
 * A toolbar filter you can type into, for lists too long to scan.
 *
 * The permission filter made the case: a plain select opened a hundred and fifty codes in one
 * scrolling column, which is a list you can only use if you already know what is in it. Typing
 * narrows it to the few that matter.
 *
 * Every match is rendered, and that is a decision rather than an oversight. Handing over ten at a
 * time as the list was scrolled read better and could not work: MUI resyncs which option is
 * highlighted whenever the *number* of options changes, and with nothing selected that sync resets
 * the highlight, which it carries out by setting the listbox scrollTop to zero. Loading more and
 * losing the reader's place were the same event - scroll to the bottom, ten more arrive, and you are
 * back at the first entry. Undoing it afterwards means fighting the library on every render for a
 * list that is a few hundred short rows at its largest.
 *
 * Empty means "all", and says so in the box rather than as a first row you have to scroll back up
 * to find. Same height, radius and border as the search field beside it.
 */
export const ToolbarSearchSelect: FC<ToolbarSearchSelectProps> = ({
                                                                      value,
                                                                      onChange,
                                                                      options,
                                                                      emptyLabel,
                                                                      width = 220,
                                                                      startAdornment,
                                                                  }) => {
    const selected = options.find((option) => option.value === value) ?? null;

    return (
        <Autocomplete
            size="small"
            openOnFocus
            options={options}
            value={selected}
            onChange={(_event, option) => onChange(option?.value ?? '')}
            isOptionEqualToValue={(option, current) => option.value === current.value}
            getOptionLabel={(option) => option.label}
            filterOptions={filter}
            // 280px is the ceiling, not the height: a phone held sideways is shorter than that, and
            // the list then runs off both ends of the screen with no way to reach either.
            ListboxProps={{style: {maxHeight: 'min(280px, 40dvh)'}}}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.value} sx={{display: 'block !important'}}>
                    <Typography sx={{fontSize: '0.875rem'}}>{option.label}</Typography>
                    {option.hint && (
                        <Typography sx={{fontSize: '0.75rem', color: theme.palette.neutral[500]}}>
                            {option.hint}
                        </Typography>
                    )}
                </Box>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={emptyLabel}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: startAdornment ?? params.InputProps.startAdornment,
                    }}
                    sx={{
                        width,
                        '& .MuiOutlinedInput-root': {
                            ...toolbarSelectSx,
                            paddingTop: 0,
                            paddingBottom: 0,
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: theme.palette.neutral[500],
                            opacity: 1,
                        },
                    }}
                />
            )}
        />
    );
};
